import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera } from "lucide-react";
import api from "../lib/api";
import { Attribution } from "../components/Attribution";
import { ChairCard } from "../components/ChairCard";
import { AdSlot } from "../components/AdSlot";

export default function Home() {
  const [cotd, setCotd] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [movements, setMovements] = useState([]);

  useEffect(() => {
    api.get("/chair-of-the-day").then((r) => setCotd(r.data)).catch(() => {});
    api.get("/chairs", { params: { limit: 8, sort: "year_asc" } }).then((r) => setFeatured(r.data.items));
    api.get("/movements").then((r) => setMovements(r.data.slice(0, 6)));
  }, []);

  const chair = cotd?.chair;

  return (
    <div>
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-10 sm:pt-16">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="overline text-accent" data-testid="cotd-label">Chair of the Day</p>
            <h1 className="serif text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-3">
              {chair ? chair.title : "A living archive of the chair."}
            </h1>
            {chair && (
              <>
                <p className="text-base text-muted-foreground mt-5 leading-relaxed max-w-md">
                  {chair.history.slice(0, 220)}…
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mt-6">
                  <span><span className="overline block">Designer</span>{chair.designer}</span>
                  <span><span className="overline block">Year</span>{chair.year}</span>
                  <span><span className="overline block">Movement</span>{chair.movement}</span>
                </div>
                <div className="flex items-center gap-4 mt-8">
                  <Link to={`/chair/${chair.slug}`} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 h-12 text-sm font-medium hover:opacity-90 transition-opacity" data-testid="cotd-view-btn">
                    View this chair <ArrowRight size={16} />
                  </Link>
                  <Link to="/chair-of-the-day/archive" className="text-sm underline underline-offset-4 hover:text-accent" data-testid="cotd-archive-link">
                    See the archive
                  </Link>
                </div>
              </>
            )}
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            {chair && (
              <div>
                <div className="hover-zoom border border-border bg-card aspect-[4/3]">
                  <img src={chair.image_url} alt={chair.title} className="w-full h-full object-cover" />
                </div>
                <Attribution chair={chair} className="mt-2" />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-20">
        <Link to="/identify" className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border border-border bg-card p-8 sm:p-12 hover:border-accent transition-colors" data-testid="identify-banner">
          <div>
            <p className="overline text-accent">Experimental</p>
            <h2 className="serif text-3xl sm:text-4xl mt-2">Spotted a chair in the wild?</h2>
            <p className="text-muted-foreground mt-2 max-w-lg">Upload a photo and our AI returns its top three best-guess matches from the catalog. Replicas and lookalikes will affect accuracy.</p>
          </div>
          <span className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 h-12 text-sm font-medium shrink-0">
            <Camera size={16} /> Identify a chair
          </span>
        </Link>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="overline">The Collection</p>
            <h2 className="serif text-3xl sm:text-4xl mt-1">Featured pieces</h2>
          </div>
          <Link to="/catalog" className="text-sm underline underline-offset-4 hover:text-accent shrink-0" data-testid="browse-all-link">Browse all</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
          {featured.map((c, i) => <ChairCard key={c.slug} chair={c} index={i} />)}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-16">
        <AdSlot height="h-28" />
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-20">
        <p className="overline">Explore by</p>
        <h2 className="serif text-3xl sm:text-4xl mt-1 mb-8">Movements</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {movements.map((m) => (
            <Link key={m.slug} to={`/movements/${m.slug}`} className="bg-card p-8 hover:bg-secondary/50 transition-colors group" data-testid={`movement-tile-${m.slug}`}>
              <div className="flex items-baseline justify-between">
                <h3 className="serif text-2xl group-hover:text-accent transition-colors">{m.name}</h3>
                <span className="text-sm text-muted-foreground">{m.chair_count}</span>
              </div>
              <p className="overline mt-1">{m.period}</p>
              <p className="text-sm text-muted-foreground mt-3 line-clamp-3">{m.summary}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
